import { Injectable, inject, signal } from '@angular/core';

import {
    Dashboard,
    DashboardsStore,
    DashboardType,
    DashboardTypes
} from '@fry/dashboards/lib';
import { ProfileStore } from '@fry/lib/profile';
import { User } from '@fry/lib/users';
import { Observable, catchError, map, of, switchMap } from 'rxjs';


export interface DashboardsWithState {
    dashboards: Dashboard[] | null;
    state: 'loading' | 'loaded' | 'error';
    error?: any;
}

export interface DashboardWithState {
    dashboard: Dashboard | null;
    state: 'loading' | 'loaded' | 'error';
    error?: any;
}

const EmptyDashboardsWithState: DashboardsWithState = {
    dashboards: null,
    state: 'loading'
};

const EmptyDashboardWithState: DashboardWithState = {
    dashboard: null,
    state: 'loading'
};

const LAST_DASHBOARD_ID_KEY = 'kzLastDashboardId';

@Injectable()
export class DashboardsService {

    private dashboardsStore = inject(DashboardsStore);
    private profileStore = inject(ProfileStore);

    private $_availableDashboards = signal<DashboardsWithState>(EmptyDashboardsWithState);
    $availableDashboards = this.$_availableDashboards.asReadonly();

    private $_selectedDashboard = signal<DashboardWithState>(EmptyDashboardWithState);
    $selectedDashboard = this.$_selectedDashboard.asReadonly();

    setDashboard(dashboardId: string) {
        const availableDashboards = this.$availableDashboards();
        if (availableDashboards.state !== 'loaded') {
            throw new Error('Dashboards are not loaded yet');
        }

        const dashboard = availableDashboards.dashboards?.find(d => d.doc._id === dashboardId);
        if (dashboard !== undefined) {
            this.$_selectedDashboard.set({ dashboard, state: 'loaded' });
            localStorage.setItem(LAST_DASHBOARD_ID_KEY, dashboard.doc._id);
        }
    };

    loadDashboards(): Observable<void> {
        return this.profileStore.getCurrent().pipe(
            map(user => user.getRoles()),
            switchMap(roles => this.loadFor(roles, DashboardTypes.dashboard))
        );
    }

    loadSummariesFor(user: User): Observable<void> {
        const roles =  user.getRoles()
        return this.loadFor(roles, DashboardTypes.summary);
    }

    private loadFor(roles: string[], dashboardType: DashboardType): Observable<void> {
        this.$_availableDashboards.set(EmptyDashboardsWithState);
        return this.dashboardsStore.getAvailableDashboards(roles, dashboardType).pipe(
            map(dashboards => {
                // When we have no dashboards we set the system default dashboard
                if (dashboards.length === 0) {
                    dashboards = [this.dashboardsStore.getDefaultDashboard()];
                }

                // Sort available Dashboards by name when we loaded them
                dashboards = dashboards.sort((a, b) => a.doc.name.localeCompare(b.doc.name));
                this.$_availableDashboards.set({ dashboards, state: 'loaded' });

                // Select the last dashboard that was selected by the user or the first one we 
                // have in available dashboards
                const lastDashboardId = localStorage.getItem(LAST_DASHBOARD_ID_KEY);
                let lastDashboard = lastDashboardId ? dashboards.find(d => d.doc._id === lastDashboardId) : undefined;
                lastDashboard = lastDashboard ?? dashboards[0];
                this.$_selectedDashboard.set({ dashboard: lastDashboard, state: 'loaded' });
            }),
            catchError(err => {
                console.error(err);
                this.$_availableDashboards.set({ dashboards: null, state: 'error', error: err });
                return of();
            })
        );
    }
}
